import React, { useState, useEffect } from 'react';
import { Plus, Search, SlidersHorizontal, X } from 'lucide-react';
import { usePersonalActions } from '../../../hooks/usePersonalActions';
import { useEmployees } from '../../../hooks/useEmployees';
import { personalActionsAPI } from '../../../api/personal-actions';
import PersonalActionsList from './PersonalActionsList';
import PersonalActionForm from './PersonalActionForm';
import PersonalActionDetails from './PersonalActionDetails';

export default function PersonalActionsModule({ deepLinkId, onDeepLinkConsumed }) {
  const [showFormModal, setShowFormModal]   = useState(false);
  const [editingAction, setEditingAction]   = useState(null);
  const [viewingAction, setViewingAction]   = useState(null);
  const [searchTerm, setSearchTerm]         = useState('');
  const [filterTipo, setFilterTipo]         = useState('');
  const [filterEstado, setFilterEstado]     = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [showFilters, setShowFilters]       = useState(false);
  const [stats, setStats]                   = useState(null);
  const [tipos, setTipos]                   = useState([]);

  const { actions, loading, error, loadActions, loadStats, loadTipos } = usePersonalActions();
  const { employees } = useEmployees('todos');

  useEffect(() => { loadInitialData(); }, [filterTipo, filterEstado, filterEmployee]); // eslint-disable-line

  // Deep-link: abrir detalle de una acción específica al navegar desde historial
  useEffect(() => {
    if (!deepLinkId) return;
    personalActionsAPI.getById(deepLinkId)
      .then(data => {
        if (data.success && data.action) {
          setViewingAction(data.action);
          onDeepLinkConsumed?.();
        }
      })
      .catch(() => { /* silencio — el item podría no existir */ });
  }, [deepLinkId]); // eslint-disable-line

  const loadInitialData = async () => {
    const filters = {};
    if (filterTipo)     filters.tipo       = filterTipo;
    if (filterEstado)   filters.estado     = filterEstado;
    if (filterEmployee) filters.employeeId = filterEmployee;
    await loadActions(filters);
    if (!stats) {
      const r = await loadStats();
      if (r.success) setStats(r.stats);
    }
    if (tipos.length === 0) {
      const r = await loadTipos();
      if (r.success) setTipos(r.tipos);
    }
  };

  const handleNewAction    = () => { setEditingAction(null); setShowFormModal(true); };
  const handleEdit         = (a) => { setEditingAction(a); setShowFormModal(true); };
  const handleView         = (a) => setViewingAction(a);
  const handleCloseForm    = () => { setShowFormModal(false); setEditingAction(null); };
  const handleCloseDetails = () => setViewingAction(null);
  const handleSaveSuccess  = () => { setShowFormModal(false); setEditingAction(null); setViewingAction(null); loadInitialData(); };
  const handleDeleteSuccess = () => loadInitialData();

  const filtered = actions.filter(a => {
    if (!searchTerm) return true;
    const q = searchTerm.toLowerCase();
    return (
      a.empleado_nombre?.toLowerCase().includes(q) ||
      a.titulo?.toLowerCase().includes(q) ||
      a.tipo_accion_nombre?.toLowerCase().includes(q) ||
      a.numero_accion?.toLowerCase().includes(q)
    );
  });

  const filtersActive = !!(filterTipo || filterEstado || filterEmployee);

  // ── Form view ────────────────────────────────────────────────
  if (showFormModal) {
    return (
      <PersonalActionForm
        action={editingAction}
        employees={employees}
        tipos={tipos}
        onSave={handleSaveSuccess}
        onCancel={handleCloseForm}
      />
    );
  }

  // ── Details view ─────────────────────────────────────────────
  if (viewingAction) {
    return (
      <PersonalActionDetails
        action={viewingAction}
        onClose={handleCloseDetails}
      />
    );
  }

  // ── List view ────────────────────────────────────────────────
  if (loading && !actions.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto mb-3" />
          <p className="text-sm text-gray-400">Cargando acciones de personal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* ── Toolbar ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between">
        <span className="text-sm text-gray-400 bg-gray-100 px-2.5 py-1 rounded-full font-medium">
          {filtered.length} de {actions.length}
        </span>
        <button
          onClick={handleNewAction}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={15} />
          Nueva acción
        </button>
      </div>

      {/* ── Search + Filter bar ──────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar acción..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400 bg-white"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-300 hover:text-gray-500"
            >
              <X size={14} />
            </button>
          )}
        </div>

        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-3 py-2 text-sm border rounded-lg transition-colors ${
            showFilters || filtersActive
              ? 'border-blue-400 text-blue-600 bg-blue-50'
              : 'border-gray-200 text-gray-500 hover:bg-gray-50'
          }`}
        >
          <SlidersHorizontal size={15} />
          Filtros
          {filtersActive && <span className="w-2 h-2 rounded-full bg-blue-500" />}
        </button>

        {stats && (
          <div className="hidden sm:flex items-center gap-2 ml-auto">
            <span className="text-xs text-gray-400">
              <span className="font-semibold text-amber-500">{stats.pendientes || 0}</span> pendientes
            </span>
            <span className="text-gray-200">|</span>
            <span className="text-xs text-gray-400">
              <span className="font-semibold text-green-600">{stats.aprobadas || 0}</span> aprobadas
            </span>
            <span className="text-gray-200">|</span>
            <span className="text-xs text-gray-400">
              <span className="font-semibold text-red-500">{stats.rechazadas || 0}</span> rechazadas
            </span>
          </div>
        )}
      </div>

      {/* ── Filter dropdowns (expandible) ───────────────────────── */}
      {showFilters && (
        <div className="flex flex-wrap items-center gap-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
          <select
            value={filterTipo}
            onChange={e => setFilterTipo(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400"
          >
            <option value="">Todos los tipos</option>
            {tipos.map(t => (
              <option key={t.codigo} value={t.codigo}>{t.nombre}</option>
            ))}
          </select>
          <select
            value={filterEstado}
            onChange={e => setFilterEstado(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400"
          >
            <option value="">Todos los estados</option>
            <option value="pendiente">Pendientes</option>
            <option value="aprobado">Aprobadas</option>
            <option value="rechazado">Rechazadas</option>
            <option value="revocado">Revocadas</option>
            <option value="completado">Completadas</option>
          </select>
          <select
            value={filterEmployee}
            onChange={e => setFilterEmployee(e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:border-blue-400"
          >
            <option value="">Todos los empleados</option>
            {employees.map(emp => (
              <option key={emp.id} value={emp.id}>{emp.nombre} {emp.apellido}</option>
            ))}
          </select>
          {filtersActive && (
            <button
              onClick={() => { setFilterTipo(''); setFilterEstado(''); setFilterEmployee(''); }}
              className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
            >
              <X size={12} /> Limpiar filtros
            </button>
          )}
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-600 text-sm">{error}</p>
        </div>
      )}

      {/* ── Table ───────────────────────────────────────────────── */}
      <PersonalActionsList
        actions={filtered}
        onEdit={handleEdit}
        onView={handleView}
        onDeleteSuccess={handleDeleteSuccess}
      />
    </div>
  );
}
